import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchActiveCampaigns,
  fetchCampaignDetail,
  fetchCampaigns,
  fetchUserTickets,
  initCampaignPayment,
  reserveCampaignTicket,
} from "../api/campaignApi";

export const campaignKeys = {
  active: ["campaigns", "active"],
  list: ["campaigns", "list"],
  detail: (campaignUuid) => ["campaigns", "detail", campaignUuid],
  tickets: ["campaigns", "tickets"],
};

export const getCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.campaigns)) return payload.campaigns;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.history)) return payload.history;
  return [];
};

export const getTotalCount = (payload, fallbackLength = 0) =>
  Number(payload?.count ?? payload?.total ?? payload?.total_count ?? fallbackLength);

// `city` is part of the query key so switching city in the location selector
// refetches instead of serving the previous city's cached campaigns.
export const useActiveCampaigns = ({ token, limit = 5, city }) =>
  useQuery({
    queryKey: [...campaignKeys.active, { limit, city: city ?? "" }],
    queryFn: () => fetchActiveCampaigns({ token, limit, city }),
    enabled: !!token,
    staleTime: 30_000,
  });

export const useCampaigns = ({ token, city }) =>
  useQuery({
    queryKey: [...campaignKeys.list, { city: city ?? "" }],
    queryFn: () => fetchCampaigns({ token, city }),
    enabled: !!token,
    staleTime: 30_000,
  });

export const useCampaignDetail = ({ campaignUuid, token }) =>
  useQuery({
    queryKey: campaignKeys.detail(campaignUuid),
    queryFn: () => fetchCampaignDetail({ campaignUuid, token }),
    enabled: !!token && !!campaignUuid,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

export const useTicketHistory = ({ token }) =>
  useQuery({
    queryKey: campaignKeys.tickets,
    queryFn: () => fetchUserTickets({ token }),
    enabled: !!token,
    staleTime: 30_000,
  });

export const useReserveCampaignTicket = ({ campaignUuid, token }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => reserveCampaignTicket({ campaignUuid, token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns", "active"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns", "list"] });
      queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignUuid) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.tickets });
    },
  });
};

export const useRetryCampaignPayment = ({ campaignUuid, token }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => initCampaignPayment({ campaignUuid, token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignUuid) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.tickets });
    },
  });
};

export const useInitCampaignPayment = useRetryCampaignPayment;
